/**
 * Goods Receipts Service
 *
 * API calls for Goods Receipts.
 * @see US-9.4 - Page Liste Receptions
 */

import { apiClient } from '@/api/client';
import type {
  GoodsReceiptListItem,
  GoodsReceiptWithDetails,
  CreateGoodsReceiptDto,
  UpdateGoodsReceiptDto,
  UpdateGoodsReceiptItemDto,
  QueryGoodsReceiptsDto,
  InitReceiptFromOrderDto,
  ApiSuccess,
  GoodsReceiptsListResponse,
  GoodsReceiptDetailResponse,
  GoodsReceiptCreateResponse,
  GoodsReceiptUpdateResponse,
  GoodsReceiptValidateResponse,
  GoodsReceiptCancelResponse,
  InitReceiptFromOrderResponse,
  GoodsReceiptsKPIs,
  PMPDisplayData,
  PMPHistoryResponse,
  QueryPMPHistoryDto,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  GoodsReceiptListItem,
  GoodsReceiptWithDetails,
  CreateGoodsReceiptDto,
  UpdateGoodsReceiptDto,
  UpdateGoodsReceiptItemDto,
  QueryGoodsReceiptsDto,
  InitReceiptFromOrderDto,
  InitReceiptFromOrderResponse,
  GoodsReceiptsKPIs,
  PMPDisplayData,
  PMPHistoryResponse,
};

export const goodsReceiptsService = {
  /**
   * List goods receipts with pagination and filters
   */
  async list(params: QueryGoodsReceiptsDto): Promise<GoodsReceiptsListResponse> {
    const response = await apiClient.get<ApiSuccess<GoodsReceiptsListResponse>>(
      '/goods-receipts',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get goods receipts KPIs
   */
  async getKPIs(startDate?: string, endDate?: string): Promise<GoodsReceiptsKPIs> {
    const response = await apiClient.get<ApiSuccess<GoodsReceiptsKPIs>>(
      '/goods-receipts/kpis',
      { params: { startDate, endDate } }
    );
    return response.data.data;
  },

  /**
   * Get single goods receipt by ID
   */
  async getById(id: string): Promise<GoodsReceiptDetailResponse> {
    const response = await apiClient.get<ApiSuccess<GoodsReceiptDetailResponse>>(
      `/goods-receipts/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new goods receipt
   */
  async create(
    data: CreateGoodsReceiptDto,
    files?: { blFiles?: File[]; invoiceFiles?: File[] }
  ): Promise<GoodsReceiptCreateResponse> {
    const hasFiles = files?.blFiles?.length || files?.invoiceFiles?.length;

    if (!hasFiles) {
      const response = await apiClient.post<ApiSuccess<GoodsReceiptCreateResponse>>(
        '/goods-receipts',
        data
      );
      return response.data.data;
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    for (const file of files.blFiles ?? []) {
      formData.append('blFiles', file);
    }
    for (const file of files.invoiceFiles ?? []) {
      formData.append('invoiceFiles', file);
    }

    const response = await apiClient.post<ApiSuccess<GoodsReceiptCreateResponse>>(
      '/goods-receipts',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  /**
   * Update a goods receipt (DRAFT only)
   */
  async update(id: string, data: UpdateGoodsReceiptDto): Promise<GoodsReceiptUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<GoodsReceiptUpdateResponse>>(
      `/goods-receipts/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a goods receipt (DRAFT only)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/goods-receipts/${id}`);
  },

  /**
   * Update an item in a goods receipt
   */
  async updateItem(
    receiptId: string,
    itemId: string,
    data: UpdateGoodsReceiptItemDto
  ): Promise<GoodsReceiptUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<GoodsReceiptUpdateResponse>>(
      `/goods-receipts/${receiptId}/items/${itemId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Remove an item from a goods receipt
   */
  async removeItem(receiptId: string, itemId: string): Promise<GoodsReceiptUpdateResponse> {
    const response = await apiClient.delete<ApiSuccess<GoodsReceiptUpdateResponse>>(
      `/goods-receipts/${receiptId}/items/${itemId}`
    );
    return response.data.data;
  },

  /**
   * Validate a goods receipt (DRAFT -> VALIDATED)
   */
  async validate(id: string): Promise<GoodsReceiptValidateResponse> {
    const response = await apiClient.post<ApiSuccess<GoodsReceiptValidateResponse>>(
      `/goods-receipts/${id}/validate`
    );
    return response.data.data;
  },

  /**
   * Cancel a goods receipt
   */
  async cancel(id: string): Promise<GoodsReceiptCancelResponse> {
    const response = await apiClient.post<ApiSuccess<GoodsReceiptCancelResponse>>(
      `/goods-receipts/${id}/cancel`
    );
    return response.data.data;
  },

  /**
   * Generate PDF bon de reception (SuperAdmin)
   */
  async generatePdf(id: string): Promise<void> {
    await apiClient.post(`/goods-receipts/${id}/generate-pdf`);
  },

  /**
   * Init receipt from purchase order
   */
  async initFromOrder(data: InitReceiptFromOrderDto): Promise<InitReceiptFromOrderResponse> {
    const response = await apiClient.post<ApiSuccess<InitReceiptFromOrderResponse>>(
      '/goods-receipts/init-from-order',
      data
    );
    return response.data.data;
  },

  /**
   * Download merged PDF of all validated receipts in a date range
   */
  async exportPdfBatch(dateFrom: string, dateTo: string): Promise<Blob> {
    const response = await apiClient.get('/goods-receipts/export-pdf', {
      params: { dateFrom, dateTo },
      responseType: 'blob',
    });
    return response.data;
  },
};

/**
 * PMP Service - Prix Moyen Pondere
 * @see US-9.8 - Widget PMP
 */
export const pmpService = {
  /**
   * Get current PMP for a raw product
   */
  async getPMP(rawProductId: string): Promise<PMPDisplayData> {
    const response = await apiClient.get<ApiSuccess<PMPDisplayData>>(
      `/raw-products/${rawProductId}/pmp`
    );
    return response.data.data;
  },

  /**
   * Get PMP history for a raw product
   */
  async getHistory(rawProductId: string, params?: QueryPMPHistoryDto): Promise<PMPHistoryResponse> {
    const response = await apiClient.get<ApiSuccess<PMPHistoryResponse>>(
      `/raw-products/${rawProductId}/pmp/history`,
      { params }
    );
    return response.data.data;
  },
};
